/* eslint-disable max-lines */
'use client';

import { useMemo, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Activity,
  Clock,
  Users,
  FileText,
  AlertCircle,
  AlertTriangle,
  Download,
  TrendingUp,
  Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { usePageHeader } from '@utils/hooks/use-page-header';
import Button from '@components/atoms/ui-kit/button.tsx';
import { Badge } from '@components/atoms/ui-kit/badge';
import { Text } from '@components/atoms/ui-kit/text';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import Modal from '@components/atoms/modals/modal.tsx';
import DateRangePicker from '@components/reseller-dashboard/date-range-picker.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import { useGetResellerUsage } from '@utils/api/resellers/use-get-reseller-usage.tsx';
import { useGetResellerStatistics } from '@utils/api/resellers/use-get-reseller-statistics.tsx';
import { downloadResellerReport } from '@utils/api/resellers/use-download-reseller-report.tsx';
import { DateRangeParams } from '@utils/types/resellers/dashboard.ts';
import { OrganizationUsage } from '@utils/types/resellers/dashboard.ts';
import { AxiosError } from 'axios';
import { useGetResellerExceedances } from '@utils/api/resellers/use-get-reseller-exceedances';
import {
  Exceedance,
  OrgExceedance
} from '@utils/types/back-office/exceedances';
import { PATHS } from '../../router/paths';

type ExceedanceRow = Exceedance &
  Pick<OrgExceedance, 'organization_id' | 'organization_name'> & { id: string };

/* ------------------------------------------------------------------ */
/*  Semantic color constants                                           */
/* ------------------------------------------------------------------ */
const DANGER_RGB = 'rgba(239, 68, 68, 1)';
const DANGER_BG_SOFT = 'rgba(239, 68, 68, 0.04)';
const DANGER_BG_MID = 'rgba(239, 68, 68, 0.08)';
const DANGER_RING = 'rgba(239, 68, 68, 0.25)';
const DANGER_BORDER = 'rgba(239, 68, 68, 0.5)';

/* ------------------------------------------------------------------ */
/*  MetricCard                                                         */
/* ------------------------------------------------------------------ */
type MetricCardProps = {
  title: string;
  value: number | string;
  format?: 'number' | 'hours' | 'percent' | 'custom';
  subtitle?: string;
  alert?: boolean;
  help?: string;
  icon?: ReactNode;
  onClick?: () => void;
};

function MetricCard({
  title,
  value,
  format = 'number',
  subtitle,
  alert,
  help,
  icon,
  onClick
}: MetricCardProps) {
  const { getColour, getBrandTint } = useThemeContext();

  const formatValue = (val: number | string): string => {
    if (typeof val !== 'number') return String(val);
    switch (format) {
      case 'hours':
        return `${val.toFixed(2)}h`;
      case 'percent':
        return `${val.toFixed(2)}%`;
      case 'number':
        return val.toLocaleString();
      default:
        return String(val);
    }
  };

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={e => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) onClick();
      }}
      className={`rounded-2xl border p-3 shadow-card transition-all ${onClick ? 'cursor-pointer hover:shadow-card-hover' : ''} ${alert ? '' : 'bg-app-soft border-hairline'}`}
      style={
        alert
          ? { backgroundColor: DANGER_BG_SOFT, borderColor: DANGER_BORDER }
          : undefined
      }
    >
      <div className="mb-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon && (
            <div
              className="flex h-7 w-7 items-center justify-center rounded-xl"
              style={{
                backgroundColor: alert ? DANGER_BG_MID : getBrandTint(0.12),
                color: alert ? DANGER_RGB : getColour('activeText'),
                border: `1px solid ${alert ? DANGER_RING : getBrandTint(0.25)}`
              }}
              aria-hidden="true"
            >
              {icon}
            </div>
          )}
          <Text
            as="span"
            size="xs"
            weight="medium"
            className="uppercase tracking-wide"
            tone="muted"
          >
            {title}
          </Text>
        </div>
        {help && (
          <div className="group relative">
            <Info size={14} className="cursor-help" />
            <Text
              as="div"
              size="xs"
              className="absolute right-0 top-5 z-10 hidden w-48 rounded-2xl p-2 shadow-pop group-hover:block bg-app-soft border border-hairline"
            >
              {help}
            </Text>
          </div>
        )}
      </div>
      <div
        className="text-xl font-bold"
        style={alert ? { color: DANGER_RGB } : undefined}
      >
        {formatValue(value)}
      </div>
      {subtitle && (
        <Text as="p" size="xs" tone="muted" className="mt-1">
          {subtitle}
        </Text>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ChartCard                                                          */
/* ------------------------------------------------------------------ */
const CHART_COLORS = [
  'rgba(43, 126, 255, 1)',
  'rgba(136, 132, 216, 1)',
  'rgba(130, 202, 157, 1)',
  'rgba(255, 198, 88, 1)',
  'rgba(255, 128, 66, 1)',
  'rgba(141, 209, 225, 1)'
];

function ChartCard({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-hairline p-4 shadow-card bg-app-soft">
      <Text as="h3" size="xs" weight="semibold" className="mb-3">
        {title}
      </Text>
      <div className="flex items-center justify-center">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ResellerDashboard                                                  */
/* ------------------------------------------------------------------ */
export default function ResellerDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();
  const [exceedanceModalOpen, setExceedanceModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeParams>({});

  const { data: exceedanceData } = useGetResellerExceedances();

  const {
    data: usageResponse,
    isLoading: isLoadingUsage,
    isFetching: isFetchingUsage,
    isError: isErrorUsage,
    error: errorUsage
  } = useGetResellerUsage(dateRange);

  const {
    data: statsResponse,
    isLoading: isLoadingStats,
    isFetching: isFetchingStats,
    isError: isErrorStats,
    error: errorStats
  } = useGetResellerStatistics(dateRange);

  const usage = usageResponse?.result;
  const stats = statsResponse?.result;
  const isLoading = isLoadingUsage || isLoadingStats;
  const isFetching = isFetchingUsage || isFetchingStats;
  const hasErrors = isErrorUsage || isErrorStats;

  const getErrorMessage = (error: unknown): string => {
    if (!error) return t('an-error-has-occurred');
    const axiosError = error as AxiosError<Record<string, string>>;
    const status = axiosError?.response?.status;
    const data = axiosError?.response?.data;
    if (status === 500) return t('server-error-please-try-again-later');
    if (status === 503) return t('service-temporarily-unavailable');
    if (status === 403 || status === 401) return t('unauthorized-access');
    if (status === 404) return t('resource-not-found');
    return String(
      data?.error_description ||
        data?.detail ||
        data?.error ||
        data?.message ||
        axiosError?.message ||
        t('an-error-has-occurred')
    );
  };

  const handleExport = async () => {
    try {
      await downloadResellerReport(dateRange);
      toast.success(t('generated-report'));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('failed-to-download-report')
      );
    }
  };

  /* ---- exceedance data ---- */
  const exceedances = exceedanceData?.result || [];
  const exceedingOrgCount = new Set(exceedances.map(e => e.organization_id))
    .size;

  const organizations = usage?.organizations || [];

  /* ---- chart data ---- */
  const hoursChartData = useMemo(
    () =>
      (usage?.organizations || []).slice(0, 10).map(org => ({
        name:
          org.organization_name.length > 15
            ? `${org.organization_name.substring(0, 12)}...`
            : org.organization_name,
        hours: Number(Number(org.total_audio_hours || 0).toFixed(2)),
        sessions: org.total_sessions
      })),
    [usage]
  );

  const errorDistributionData = useMemo(
    () =>
      (usage?.organizations || [])
        .filter(org => org.error_sessions > 0)
        .slice(0, 5)
        .map(org => ({
          name:
            org.organization_name.length > 20
              ? `${org.organization_name.substring(0, 17)}...`
              : org.organization_name,
          errors: org.error_sessions
        })),
    [usage]
  );

  /* ---- exceedance table columns ---- */
  const exceedanceColumns = useMemo<DataGridColumn<ExceedanceRow>[]>(
    () => [
      {
        id: 'organization_name',
        accessorKey: 'organization_name',
        header: t('organization'),
        minWidth: 180,
        enableSorting: true,
        cell: info => {
          const row = info.row.original;
          return (
            <button
              className="text-left underline hover:opacity-80"
              onClick={() =>
                navigate(PATHS(String(row.organization_id)).RESELLER_CUSTOMER)
              }
            >
              <Text as="span" size="sm" tone="brand">
                {row.organization_name}
              </Text>
            </button>
          );
        }
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: t('type'),
        maxWidth: 140,
        cell: info => {
          const type = info.getValue() as string;
          const isTranslation = type === 'translation';
          return (
            <Badge color={isTranslation ? 'blue' : 'amber'}>{t(type)}</Badge>
          );
        }
      },
      {
        id: 'limit',
        accessorKey: 'limit',
        header: t('limit'),
        maxWidth: 120,
        enableSorting: true,
        cell: info => (
          <Text as="span" size="sm">
            {Number(info.getValue() || 0).toLocaleString()}
          </Text>
        )
      },
      {
        id: 'used',
        accessorKey: 'used',
        header: t('used'),
        maxWidth: 120,
        enableSorting: true,
        cell: info => (
          <Text as="span" size="sm">
            {Number(info.getValue() || 0).toLocaleString()}
          </Text>
        )
      },
      {
        id: 'exceeded_by',
        accessorKey: 'exceeded_by',
        header: t('exceeded-by'),
        maxWidth: 130,
        enableSorting: true,
        cell: info => (
          <span className="font-semibold" style={{ color: DANGER_RGB }}>
            {Number(info.getValue() || 0).toLocaleString()}
          </span>
        )
      },
      {
        id: 'plan_sku',
        accessorKey: 'plan_sku',
        header: t('plan'),
        maxWidth: 160,
        enableSorting: true
      },
      {
        id: 'period_start',
        accessorKey: 'period_start',
        header: t('period'),
        maxWidth: 180,
        enableSorting: true,
        cell: info => {
          const row = info.row.original;
          return (
            <Text as="span" size="sm">
              {row.period_start} → {row.period_end}
            </Text>
          );
        }
      }
    ],
    [t, navigate]
  );

  const exceedanceRows: ExceedanceRow[] = useMemo(
    () =>
      exceedances.flatMap(item =>
        item.exceedances.map((exc, idx) => ({
          ...exc,
          organization_id: item.organization_id,
          organization_name: item.organization_name,
          id: `${item.organization_id}-${exc.type}-${idx}`
        }))
      ),
    [exceedances]
  );

  usePageHeader({
    title: t('reseller-dashboard'),
    description: usage?.reseller_name || '',
    actions: (
      <div className="flex items-center gap-2">
        <DateRangePicker value={dateRange} onChange={setDateRange} />
        {isFetching && (
          <div
            className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"
            style={{ borderColor: getColour('activeText') }}
          />
        )}
        <Button
          onClick={handleExport}
          variant="secondary"
          size="small"
          disabled={isFetching}
          icon={<Download size={16} />}
          label={t('export-excel')}
          title={t('export-excel')}
        />
      </div>
    )
  });

  /* ---- loading ---- */
  if (isLoading) {
    return (
      <div className="bg-app min-h-screen">
        <div className="space-y-5 px-4 pb-6 md:px-0 pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="card" />
            ))}
          </div>
          <Skeleton variant="table" rows={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-app min-h-screen">
      <div className="space-y-5 px-4 pb-6 md:px-0">
        {/* ── Errors ── */}
        {hasErrors && (
          <div className="space-y-2">
            {isErrorUsage && (
              <ErrorBanner
                title={t('usage-data-error')}
                message={getErrorMessage(errorUsage)}
              />
            )}
            {isErrorStats && (
              <ErrorBanner
                title={t('statistics-data-error')}
                message={getErrorMessage(errorStats)}
              />
            )}
          </div>
        )}

        {/* ── Key Metrics ── */}
        {!isErrorStats && (
          <section>
            <SectionLabel text={t('overview')} />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
              <MetricCard
                title={t('total-users')}
                value={stats?.total_registered_users || 0}
                icon={<Users size={14} />}
                subtitle={`${stats?.active_users_last_7_days || 0} ${t('active-last-7-days')}`}
              />
              <MetricCard
                title={t('total-hours')}
                value={stats?.total_transcribe_hours || 0}
                format="hours"
                icon={<Clock size={14} />}
              />
              <MetricCard
                title={t('total-sessions')}
                value={stats?.total_sessions || 0}
                icon={<FileText size={14} />}
                subtitle={`${stats?.finished_sessions || 0} ${t('finished')}`}
              />
              <MetricCard
                title={t('error-rate')}
                value={stats?.error_rate || 0}
                format="percent"
                icon={<AlertCircle size={14} />}
                alert={(stats?.error_rate || 0) > 5}
              />
              <MetricCard
                title={t('exceedances')}
                value={exceedingOrgCount}
                icon={<AlertTriangle size={14} />}
                subtitle={
                  exceedingOrgCount > 0
                    ? `${exceedingOrgCount} ${t('orgs-over-limit')}`
                    : t('all-within-limits')
                }
                alert={exceedingOrgCount > 0}
                onClick={() => setExceedanceModalOpen(true)}
              />
            </div>
          </section>
        )}

        {/* ── Performance Metrics ── */}
        {!isErrorStats && (
          <section>
            <SectionLabel text={t('performance')} />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <MetricCard
                title={t('avg-tat')}
                value={stats?.avg_tat_hours || 0}
                format="hours"
                icon={<Activity size={14} />}
                help={t('average-time-from-upload-to-completion')}
              />
              <MetricCard
                title={t('median-tat-p50')}
                value={stats?.p50_tat_hours || 0}
                format="hours"
                icon={<TrendingUp size={14} />}
              />
              <MetricCard
                title={t('p90-tat')}
                value={stats?.p90_tat_hours || 0}
                format="hours"
                icon={<TrendingUp size={14} />}
              />
              <MetricCard
                title={t('rtf')}
                value={stats?.rtf_weighted || 0}
                format="custom"
                subtitle={t('lower-is-better')}
                help={t('real-time-factor-processing-time-audio-duration')}
              />
            </div>
          </section>
        )}

        {/* ── Charts ── */}
        {!isErrorUsage && (
          <section>
            <SectionLabel text={t('analytics')} />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <ChartCard title={t('top-organizations-by-hours')}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={hoursChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      label={{
                        value: t('hours'),
                        angle: -90,
                        position: 'insideLeft'
                      }}
                    />
                    <Tooltip />
                    <Bar dataKey="hours" fill={CHART_COLORS[0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title={t('error-distribution')}>
                {errorDistributionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={errorDistributionData}
                        dataKey="errors"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={70}
                        label
                      >
                        {errorDistributionData.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Text
                    as="div"
                    tone="muted"
                    className="flex h-[280px] items-center justify-center"
                  >
                    {t('no-errors-to-display')}
                  </Text>
                )}
              </ChartCard>
            </div>
          </section>
        )}

        {/* ── Organizations Cards ── */}
        {!isErrorUsage && organizations.length > 0 && (
          <section>
            <SectionLabel
              text={`${t('sub-organizations')} (${usage?.total_organizations || 0})`}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {organizations.map(org => (
                <OrgCard
                  key={org.organization_id}
                  org={org}
                  onClick={() =>
                    navigate(
                      PATHS(String(org.organization_id)).RESELLER_CUSTOMER
                    )
                  }
                />
              ))}
            </div>
          </section>
        )}

        {/* ── Exceedance Modal ── */}
        <Modal
          isOpen={exceedanceModalOpen}
          onClose={() => setExceedanceModalOpen(false)}
          title={
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} style={{ color: DANGER_RGB }} />
              <span>
                {t('plan-limit-exceedances')} ({exceedances.length})
              </span>
            </div>
          }
          caption={t('organizations-exceeding-plan-limits')}
          size="x-large"
        >
          <DataGridV2<ExceedanceRow>
            columns={exceedanceColumns}
            data={exceedanceRows}
            enablePagination
            enableSorting
            enableRowSelection={false}
          />
        </Modal>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Small helpers                                                      */
/* ------------------------------------------------------------------ */
function SectionLabel({ text }: { text: string }) {
  return (
    <Text
      as="h2"
      size="xs"
      weight="semibold"
      tone="muted"
      className="mb-3 uppercase tracking-wider"
    >
      {text}
    </Text>
  );
}

function OrgCard({
  org,
  onClick
}: {
  org: OrganizationUsage;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const errorRate = Number(org.error_rate) || 0;
  const hasHighErrors = errorRate > 5;

  return (
    <button
      onClick={onClick}
      className="flex flex-col rounded-2xl border border-hairline p-3 text-left shadow-card transition-all hover:shadow-card-hover bg-app-soft"
    >
      <Text
        as="p"
        size="sm"
        weight="semibold"
        className="mb-2 truncate"
        title={org.organization_name}
      >
        {org.organization_name}
      </Text>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <StatLine
          label={t('hours')}
          value={`${Number(org.total_audio_hours || 0).toFixed(2)}h`}
        />
        <StatLine label={t('sessions')} value={String(org.total_sessions)} />
        <StatLine label={t('finished')} value={String(org.finished_sessions)} />
        <StatLine
          label={t('errors')}
          value={String(org.error_sessions)}
          alert={org.error_sessions > 0}
        />
      </div>
      {hasHighErrors && (
        <Badge color="red" className="mt-2 w-fit">
          {t('error-rate')}: {errorRate.toFixed(2)}%
        </Badge>
      )}
    </button>
  );
}

function StatLine({
  label,
  value,
  alert
}: {
  label: string;
  value: string;
  alert?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <Text as="span" size="xs" tone="muted">
        {label}
      </Text>
      <Text
        as="span"
        size="xs"
        weight={alert ? 'semibold' : undefined}
        style={alert ? { color: DANGER_RGB } : undefined}
      >
        {value}
      </Text>
    </div>
  );
}

function ErrorBanner({ title, message }: { title: string; message: string }) {
  return (
    <div
      className="flex items-start gap-3 rounded-2xl border p-4"
      style={{
        backgroundColor: DANGER_BG_SOFT,
        borderColor: DANGER_RING
      }}
    >
      <AlertCircle
        className="mt-0.5 h-5 w-5 flex-shrink-0"
        style={{ color: DANGER_RGB }}
      />
      <div className="flex-1">
        <Text as="h3" size="sm" weight="semibold" style={{ color: DANGER_RGB }}>
          {title}
        </Text>
        <Text as="p" size="sm" className="mt-1 opacity-80">
          {message}
        </Text>
      </div>
    </div>
  );
}
